import React from "react";
import { Box } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";

import CheckboxColumn from "components/Table/CheckboxColumn";

import { linkSpeedOptions, portStaSpeedMap } from "../../const";

const columnHelper = createColumnHelper();

const getSwitchesColumns = (showCheckbox = true) => {
  const baseColumns = [
    columnHelper.accessor("deviceName", {
      header: "Device Name",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor("portIndex", {
      header: "Port",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),

    columnHelper.accessor("state", {
      header: "State",
      cell: (info) => {
        const value = info.getValue();

        if (!value || value === "-") {
          return <Box>-</Box>;
        }

        if (
          React.isValidElement(value) &&
          value.type === "img" &&
          !value.props.src
        ) {
          return <Box>-</Box>;
        }

        return <Box>{value}</Box>;
      },
    }),
    columnHelper.accessor("portName", {
      header: "Port Name",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("portProfileName", {
      header: "Port Profile",
      cell: (info) => {
        const value = info.getValue();
        const isProfileValid = value != null && value !== "No Profile";

        return <Box>{isProfileValid ? value : "-"}</Box>
      },
    }),
    columnHelper.accessor("speedLabel", {
      header: "Speed",
      cell: (info) => {
        const row = info.row.original;
        const selectedProfile = row.selectedProfile;
        const switchPort = row.switchPort;
        const fallbackSpeed = row.speed;

        let label = "-";

        if(!selectedProfile?.isDefault){
          const matchedProfileSpeed = linkSpeedOptions.find(speed => speed.value === selectedProfile?.speed);
          label = matchedProfileSpeed?.label ?? "-";
        }else{
          if (!switchPort) {
            const matchedSpeed = linkSpeedOptions.find(opt => opt.value === fallbackSpeed);
            label = matchedSpeed ? matchedSpeed.label : "-";
          } else if (switchPort.portStaStatus === 2) {
            label = "-";
          } else {
            label = portStaSpeedMap[switchPort.portStaSpeed] || "-";
          }
        }

        return <Box>{label}</Box>;
      },
    }),

    columnHelper.accessor("portTypeLabel", {
      header: "Port Type",
      cell: (info) => {
        const selectedProfile = info.row.original.selectedProfile;
        const value = selectedProfile?.portType ?? info.getValue();
        const newValue = value ? value.charAt(0).toUpperCase() + value.slice(1) : "-";

        return <Box>{newValue}</Box>
      },
    }),
    columnHelper.accessor("aggregate", {
      header: "Aggregate",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("PVID", {
      header: "PVID",
      cell: (info) => {
        const selectedProfile = info.row.original.selectedProfile;
        const value = selectedProfile?.PVID ?? info.getValue() ?? "-";
        return <Box>{value}</Box>
      },
    }),
    columnHelper.accessor("allowVlan", {
      header: "Allowed VLANS",
      cell: (info) => {
        const portType = info.row.original.portType;
        if (portType === "access") {
          return <Box>-</Box>;
        }

        const selectedProfile = info.row.original.selectedProfile;
        const value = selectedProfile?.allowVlan ?? info.getValue() ?? "-";

        if (Array.isArray(value) && value.length === 0) return <Box>-</Box>;
        if (Array.isArray(value)) return <Box>{value.join(", ")}</Box>;
        return <Box>{value ?? "-"}</Box>;
      },
    }),
    columnHelper.accessor("poe", {
      header: "PoE",
      cell: (info) => {
        const selectedProfile = info.row.original.selectedProfile;
        const value = selectedProfile?.poeState ?? info.getValue();
        return <Box>{value === true ? "Enabled" : "Disabled"}</Box>;
      },
    }),
    columnHelper.accessor("poePowerUsage", {
      header: "PoE Power Usage (W)",
      cell: (info) => {
        const poe = info.row.original.poe;
        if (poe !== true || (poe && parseInt(info.getValue(), 10) < 0)) {
          return <Box>-</Box>;
        }

        return <Box>{info.getValue() || "-"}</Box>;
      },
    }),
    columnHelper.accessor("modelName", {
      header: "Model Name",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("rstp", {
      header: "RSTP",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("lbdState", {
      header: "LBD",
      cell: (info) => {
        const selectedProfile = info.row.original.selectedProfile;
        const value = selectedProfile?.lbdState ?? info.getValue();

        return <Box>{value === true ? "Enabled" : "Disabled"}</Box>;
      },
    }),
    columnHelper.accessor("ddpState", {
      header: "DDP",
      cell: (info) => {
        const selectedProfile = info.row.original.selectedProfile;
        const value = selectedProfile?.ddpState ?? info.getValue();

        return <Box>{value === true ? "Enabled" : "Disabled"}</Box>;
      },
    }),
    columnHelper.accessor("lldp", {
      header: "LLDP",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("stpGuard", {
      header: "STP Guard",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("portSchedule", {
      header: "Port Schedule",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("poeSchedule", {
      header: "PoE Schedule",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("accessPolicies", {
      header: "Access Policies",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("mirror", {
      header: "Mirror",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("portIsolate", {
      header: "Port Isolate",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("tags", {
      header: "Tags",
      cell: (info) => {
        const value = info.getValue();
        if (Array.isArray(value) && value.length === 0) return <Box>-</Box>;
        if (Array.isArray(value)) return <Box>{value.join(", ")}</Box>;
        return <Box>{value ?? "-"}</Box>;
      },
    }),
    columnHelper.accessor("flowControl", {
      header: "Flow Control",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("numberOfIGMPGroup", {
      header: "Number of IGMP Group",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("deviceIp", {
      header: "Device Ip",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("txTraffic", {
      header: "Tx Traffic",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("rxTraffic", {
      header: "Rx Traffic",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("txMulticast", {
      header: "Tx Multicast",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("rxMulticast", {
      header: "Rx Multicast",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("txBroadcast", {
      header: "Tx Broadcast",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("rxBroadcast", {
      header: "Rx Broadcast",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("txPackets", {
      header: "Tx Packets",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("rxPackets", {
      header: "Rx Packets",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("totalTraffic", {
      header: "Total Traffic",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
  ];

  return showCheckbox
    ? [CheckboxColumn(columnHelper), ...baseColumns]
    : baseColumns;
};

export default getSwitchesColumns;
